import type { ApiClient } from '@carbonwebmail/api-client'
import type { StuckMessageDto, StuckStatus } from '@carbonwebmail/contracts'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { count, when } from '../../ui/format.js'
import { TableSkeleton } from '../../ui/Pager.js'
import { Table, Td, Tr } from '../../ui/Table.js'

import { useStuckMessages } from './useStuckMessages.js'

/**
 * Messages no worker will move again.
 *
 * Two states, and the difference between them is the whole reason this is one
 * component with a branch rather than two screens.
 *
 * A **dead** message ran out of retries. Replaying it is ordinary: nothing
 * ambiguous happened, it simply failed enough times, and the row is put back
 * with its attempts reset.
 *
 * An **unknown** message is one the provider never answered for. It may already
 * have been delivered, which is exactly why nothing retries it automatically
 * (I7) — and why the two buttons here are phrased as *what you found when you
 * looked*, not as *what you would like to happen*. Choosing "it never arrived"
 * sends the message again, and the only thing making that safe is that somebody
 * checked. The copy says so, because a bare pair of buttons would invite a
 * guess and a guess here is a duplicate email.
 */
export function StuckMessages({
  client,
  status,
}: {
  readonly client: ApiClient
  readonly status: StuckStatus
}) {
  const { state, notice, acting, replay, resolve, retry } = useStuckMessages({ client, status })
  const dead = status === 'dead'

  return (
    <section className="mb-8">
      <h2 className="font-bold text-[15px] text-t1">
        {dead ? 'Dead letters' : 'Waiting to be checked'}
      </h2>
      <p className="mt-1 mb-3 text-[13px] text-t2">
        {dead
          ? 'Out of retries. Nothing will send these until you put them back.'
          : 'The provider never said what happened, so these were never retried — sending again blindly is how somebody gets the same email twice. Look the message up at your provider, then say what you found.'}
      </p>

      {notice === undefined ? null : <Alert tone={notice.tone}>{notice.text}</Alert>}

      <Async state={state} skeleton={<TableSkeleton />} onRetry={() => void retry()}>
        {(result) =>
          result.rows.length === 0 ? (
            <p className="rounded-card border border-line bg-surface px-4 py-6 text-center text-[13px] text-t3">
              {dead
                ? 'Nothing has run out of retries.'
                : 'Every send has had a clear answer from the provider.'}
            </p>
          ) : (
            <>
              <Table
                head={
                  dead
                    ? ['When', 'To', 'Campaign', 'Tries', 'Why it stopped', '']
                    : ['When', 'To', 'Campaign', 'Message id', 'What we were told', '']
                }
                minWidth={840}
              >
                {result.rows.map((row) => (
                  <Row
                    key={row.queueEntryId}
                    row={row}
                    dead={dead}
                    acting={acting === row.queueEntryId}
                    onReplay={replay}
                    onResolve={resolve}
                  />
                ))}
              </Table>
              {result.total > result.rows.length ? (
                <p className="mt-2 text-[12px] text-t3">
                  Showing {count(result.rows.length)} of {count(result.total)}. Clear these and the
                  rest will follow.
                </p>
              ) : null}
            </>
          )
        }
      </Async>
    </section>
  )
}

function Row({
  row,
  dead,
  acting,
  onReplay,
  onResolve,
}: {
  readonly row: StuckMessageDto
  readonly dead: boolean
  readonly acting: boolean
  readonly onReplay: (queueEntryId: string) => Promise<void>
  readonly onResolve: (queueEntryId: string, as: 'delivered' | 'not-delivered') => Promise<void>
}) {
  return (
    <Tr>
      <Td>{when(row.since)}</Td>
      <Td strong>{row.email}</Td>
      {/* Transactional mail belongs to no campaign, and an em dash says that
          more honestly than an empty cell. */}
      <Td>{row.campaignName ?? '—'}</Td>
      {dead ? <Td>{count(row.attempts)}</Td> : <Td mono>{row.messageId}</Td>}
      <Td>{row.lastError ?? '—'}</Td>
      <Td>
        {dead ? (
          <button
            type="button"
            disabled={acting}
            onClick={() => void onReplay(row.queueEntryId)}
            className="rounded-pill border border-brand px-3 py-1 font-semibold text-[12px] text-brand hover:bg-brand-soft disabled:cursor-not-allowed disabled:opacity-60"
          >
            {acting ? 'Sending…' : 'Send again'}
          </button>
        ) : (
          <span className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={acting}
              onClick={() => void onResolve(row.queueEntryId, 'delivered')}
              className="rounded-pill border border-line px-3 py-1 font-semibold text-[12px] text-t2 hover:bg-line-2 hover:text-t1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              It arrived
            </button>
            {/*
              This one sends the message again. Said as a finding rather than an
              action, because the safety of the re-send rests entirely on
              somebody having actually looked.
            */}
            <button
              type="button"
              disabled={acting}
              onClick={() => void onResolve(row.queueEntryId, 'not-delivered')}
              className="rounded-pill border border-brand px-3 py-1 font-semibold text-[12px] text-brand hover:bg-brand-soft disabled:cursor-not-allowed disabled:opacity-60"
            >
              It never arrived — send it
            </button>
          </span>
        )}
      </Td>
    </Tr>
  )
}
